import { http } from "~/services/api.services";
import { getGenericURL } from "~/utils/basic";
import {
  cacheZoningPage,
  getCachedZoningPage,
  clearZoningCache,
} from "~/utils/zoning-idb";

// ==================== CONFIGURATION ====================

/**
 * Number of documents to fetch immediately before showing UI
 * Can be overridden via environment variable VITE_ZONING_FIRST_BATCH_SIZE
 */
const FIRST_BATCH_SIZE = parseInt(
  import.meta.env.VITE_ZONING_FIRST_BATCH_SIZE || "5",
  10
);

/**
 * Maximum cache age in milliseconds (default: 24 hours)
 */
const MAX_CACHE_AGE = parseInt(
  import.meta.env.VITE_ZONING_MAX_CACHE_AGE || "86400000",
  10
);

/**
 * Number of concurrent requests when background loading
 */
const CONCURRENT_REQUESTS = 3;

// ==================== TYPES ====================

export interface FirstBatchResult {
  imageUrls: string[];
  jsonData: any;
  allJsonData: any[];
  totalPages: number;
  cachedPages: number[];
}

export interface LoadingProgress {
  taskId: string;
  total: number;
  cached: number[];
  pending: number[];
  isComplete: boolean;
}

// ==================== STATE MANAGEMENT ====================

/**
 * Track loading progress for each task
 */
const loadingStates = new Map<string, LoadingProgress>();

/**
 * Abort controllers for cancellable requests
 */
const abortControllers = new Map<string, AbortController>();

// ==================== PUBLIC FUNCTIONS ====================

/**
 * Fetch the first batch of zoning documents synchronously
 * This function blocks until the first batch is loaded, then returns immediately
 *
 * @param taskId - Unique identifier for the task
 * @param imageUrls - Array of image URLs to fetch
 * @param jsonUrls - Array of JSON URLs to fetch
 * @returns First batch data with image URLs, JSON data, and metadata
 */
export async function fetchFirstBatch(
  taskId: string,
  imageUrls: string[],
  jsonUrls: string[]
): Promise<FirstBatchResult> {
  // Validate inputs
  if (!imageUrls?.length || !jsonUrls?.length) {
    throw new Error("No zoning documents to fetch");
  }

  if (imageUrls.length !== jsonUrls.length) {
    throw new Error("Image and JSON URL arrays must have the same length");
  }

  const totalDocs = imageUrls.length;
  const batchSize = Math.min(FIRST_BATCH_SIZE, totalDocs);

  // Initialize loading state
  updateLoadingState(taskId, {
    taskId,
    total: totalDocs,
    cached: [],
    pending: Array.from({ length: totalDocs }, (_, i) => i),
    isComplete: totalDocs <= batchSize,
  });

  // Clear old cache for this task (fresh start)
  await clearZoningCache(taskId);

  // Fetch first batch in parallel
  const firstBatchPromises: Promise<{
    index: number;
    url: string;
    json: any;
  }>[] = [];

  for (let i = 0; i < batchSize; i++) {
    firstBatchPromises.push(fetchSinglePage(taskId, i, imageUrls[i], jsonUrls[i]));
  }

  const results = await Promise.all(firstBatchPromises);

  // Update loading state with cached pages
  updateLoadingState(taskId, {
    cached: results.map((r) => r.index),
    pending: Array.from({ length: totalDocs - batchSize }, (_, i) => i + batchSize),
  });

  return {
    imageUrls: results.map((r) => r.url),
    jsonData: results[0]?.json || null,
    allJsonData: results.map((r) => r.json),
    totalPages: totalDocs,
    cachedPages: results.map((r) => r.index),
  };
}

/**
 * Fetch remaining pages asynchronously in the background
 * This function runs in parallel batches and doesn't block the UI
 *
 * @param taskId - Unique identifier for the task
 * @param imageUrls - Array of all image URLs
 * @param jsonUrls - Array of all JSON URLs
 * @param startIndex - Index to start fetching from
 */
export async function fetchRemainingPages(
  taskId: string,
  imageUrls: string[],
  jsonUrls: string[],
  startIndex: number
): Promise<void> {
  const totalDocs = imageUrls.length;

  if (startIndex >= totalDocs) {
    // Nothing to fetch
    updateLoadingState(taskId, { isComplete: true });
    return;
  }

  // Create abort controller for this task
  const controller = new AbortController();
  abortControllers.set(taskId, controller);

  try {
    // Process remaining pages in batches
    for (let i = startIndex; i < totalDocs; i += CONCURRENT_REQUESTS) {
      // Check if aborted
      if (controller.signal.aborted) {
        console.log(`Background loading aborted for task ${taskId}`);
        return;
      }

      // Create batch of concurrent requests
      const batchEnd = Math.min(i + CONCURRENT_REQUESTS, totalDocs);
      const batchPromises: Promise<void>[] = [];

      for (let j = i; j < batchEnd; j++) {
        batchPromises.push(
          fetchSinglePage(taskId, j, imageUrls[j], jsonUrls[j]).then(
            (result) => {
              // Update loading state with newly cached page
              const state = loadingStates.get(taskId);
              if (state) {
                state.cached.push(j);
                state.pending = state.pending.filter((p) => p !== j);
              }
            }
          )
        );
      }

      // Wait for this batch to complete
      await Promise.all(batchPromises);
    }

    // Mark as complete
    updateLoadingState(taskId, { isComplete: true });
    console.log(`Background loading complete for task ${taskId}`);
  } catch (error) {
    console.error(`Background loading failed for task ${taskId}:`, error);
  } finally {
    abortControllers.delete(taskId);
  }
}

/**
 * Get a cached page by taskId and pageIndex
 * Returns the cached data if available and not expired, otherwise null
 *
 * @param taskId - Unique identifier for the task
 * @param pageIndex - Index of the page (0-based)
 * @returns Cached page data or null
 */
export async function getCachedPage(
  taskId: string,
  pageIndex: number
): Promise<{ imageUrl: string; jsonData: any } | null> {
  const cached = await getCachedZoningPage(taskId, pageIndex, MAX_CACHE_AGE);
  if (cached) {
    return {
      imageUrl: cached.imageUrl,
      jsonData: cached.jsonData,
    };
  }
  return null;
}

/**
 * Get the current loading progress for a task
 *
 * @param taskId - Unique identifier for the task
 * @returns Loading progress info
 */
export function getLoadingProgress(taskId: string): LoadingProgress | null {
  return loadingStates.get(taskId) || null;
}

/**
 * Cancel background loading for a specific task
 *
 * @param taskId - Unique identifier for the task to cancel
 */
export function cancelLoading(taskId: string): void {
  const controller = abortControllers.get(taskId);
  if (controller) {
    controller.abort();
    abortControllers.delete(taskId);
  }
  loadingStates.delete(taskId);
}

/**
 * Clear all loading states (useful for cleanup)
 */
export function clearAllLoadingStates(): void {
  abortControllers.forEach((controller) => controller.abort());
  abortControllers.clear();
  loadingStates.clear();
}

// ==================== HELPER FUNCTIONS ====================

/**
 * Fetch a single page and cache it
 */
async function fetchSinglePage(
  taskId: string,
  index: number,
  imageUrl: string,
  jsonUrl: string
): Promise<{ index: number; url: string; json: any }> {
  try {
    const [imageResponse, jsonResponse] = await Promise.all([
      http.get(getGenericURL(imageUrl)),
      http.get(getGenericURL(jsonUrl)),
    ]);

    const imageBlob = await imageResponse.blob();
    const url = URL.createObjectURL(imageBlob);
    const json = await jsonResponse.json();

    // Cache to IndexedDB
    await cacheZoningPage(taskId, index, url, json);

    return { index, url, json };
  } catch (error) {
    console.error(`Failed to fetch page ${index} for task ${taskId}:`, error);
    throw error;
  }
}

/**
 * Update the loading state for a task
 */
function updateLoadingState(
  taskId: string,
  updates: Partial<LoadingProgress>
): void {
  const existing = loadingStates.get(taskId);
  if (existing) {
    loadingStates.set(taskId, { ...existing, ...updates });
  } else {
    loadingStates.set(taskId, {
      taskId,
      total: 0,
      cached: [],
      pending: [],
      isComplete: false,
      ...updates,
    });
  }
}